import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ChangeLangModalComponent } from '@vinnova/auth';
import { TranslateService } from '@vinnova/translation';
import { ModalService } from '@vinnova/ui';
import { map, Observable, take } from 'rxjs';
import { environment } from '../../environment';
import { NavigationService } from '../../services/navigation.service';

@Component({
  selector: 'lib-site-header',
  templateUrl: './site-header.component.html',
  styleUrls: ['./site-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SiteHeaderComponent implements OnInit, OnDestroy {
  site = input<EtjansterSites>();
  sitesToShow = input<EtjansterSites[]>();
  authenticated = input<boolean>();
  hideToolbar = input<boolean>();
  hideLogout = input<boolean>();
  disableLogoLink = input<boolean>();
  offCanvasMobileNavOpen: boolean;
  EtjansterSites = EtjansterSites;
  ansokanGuideUrl$: Observable<string>;

  private isBedomare = false;

  _offCanvasMobileNav: HTMLElement;
  @ViewChild('offCanvasMobileNav')
  set offCanvasMobileNav(value: ElementRef) {
    if (!value) {
      return;
    }
    this._offCanvasMobileNav = value.nativeElement;
    this.openOffCanvasTop = this.openOffCanvasTop.bind(this);
    this.closeOffCanvasTop = this.closeOffCanvasTop.bind(this);
    value.nativeElement.addEventListener(
      'show.bs.offcanvas',
      this.openOffCanvasTop,
    );
    value.nativeElement.addEventListener(
      'hide.bs.offcanvas',
      this.closeOffCanvasTop,
    );
  }

  private translateService = inject(TranslateService);
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  public env = inject(environment);
  links = computed(() => (this.sitesToShow() ? this.getLinks() : []));

  ngOnInit(): void {
    this.ansokanGuideUrl$ = this.translateService.langChanges$.pipe(
      map((lang) => {
        return this.env.externalLinks.ansokanGuide[lang];
      }),
    );

    this.authService
      .getIsBedomare()
      .pipe(take(1))
      .subscribe((isBedomare) => {
        this.isBedomare = isBedomare;
      });
  }

  ngOnDestroy(): void {
    this._offCanvasMobileNav?.removeEventListener(
      'show.bs.offcanvas',
      this.openOffCanvasTop,
    );
    this._offCanvasMobileNav?.removeEventListener(
      'hide.bs.offcanvas',
      this.closeOffCanvasTop,
    );
  }

  public toggleLang() {
    const newLang = this.translateService.activeLang === 'en' ? 'sv' : 'en';
    if (newLang === 'en') {
      if (this.authenticated()) {
        this.modalService.show(ChangeLangModalComponent);
      } else {
        sessionStorage.setItem('lang', 'en');
      }
    } else {
      sessionStorage.removeItem('lang');
    }
    this.translateService.changeActiveLang(newLang);
  }

  private getLinks() {
    const linkData = [
      {
        site: EtjansterSites.Start,
        name: 'start',
        url: this.env.etjansterCommonUrl,
        base: '/',
      },
      {
        site: EtjansterSites.Ansok,
        name: 'applications',
        url: this.env.ansokanUrl,
        base: '/',
      },
      {
        site: EtjansterSites.Projekt,
        name: 'projects',
        url: this.env.projektUrl,
        base: '/',
      },
      {
        site: EtjansterSites.Bedom,
        name: 'assessments',
        url: this.env.bpUrl,
        base: '/',
      },
      {
        site: EtjansterSites.MinProfil,
        name: 'my_profile',
        url: this.env.etjansterCommonUrl + '/profil',
        base: '/profil',
      },
    ];

    const links = [];
    if (!this.sitesToShow()) {
      return;
    }
    for (const link of this.sitesToShow()) {
      const d = linkData.find((d) => d.site === link);
      const active = this.site() === link;
      if (link === EtjansterSites.Bedom && !this.isBedomare) {
        continue;
      }
      links.push({ ...d, active });
    }
    return links;
  }

  navigate(url: string) {
    this.navigationService.url = url;
    if (
      this.site() === EtjansterSites.Ansok &&
      this.navigationService.ansokanGreenNotKlarmarkerad
    ) {
      this.navigationService.confirmNavigation();
    } else if (
      this.site() === EtjansterSites.MinProfil &&
      this.navigationService.bedomareProfilInteSparad
    ) {
      this.navigationService.confirmNavigation();
    } else {
      globalThis.location.href = url;
    }
  }

  refreshPage() {
    if (this.site() === EtjansterSites.MinProfil) {
      globalThis.location.reload();
    } else {
      this.router
        .navigate(['/'], {
          onSameUrlNavigation: 'reload',
          skipLocationChange: false,
        })
        .then((navigated) => {
          if (navigated) {
            globalThis.location.reload();
          }
        });
    }
  }

  private openOffCanvasTop() {
    const header = document.querySelector('.header-main') as HTMLElement;
    this._offCanvasMobileNav.style.top =
      header.getBoundingClientRect().top + header.offsetHeight + 'px';
    this.offCanvasMobileNavOpen = true;
    this.cdr.markForCheck();
  }

  private closeOffCanvasTop() {
    this.offCanvasMobileNavOpen = false;
    this.cdr.markForCheck();
  }
}

export enum EtjansterSites {
  Ansok,
  Bedom,
  Projekt,
  MinProfil,
  Start,
  Forhandsgranska,
}
